import emit from '../../common/events';
import store from '../../store/store';
import { BaseComponent } from '../base-component/base-component';
import { EbookUrl } from './interfaces/mhp-interface';
import { StoreState } from '../../store/store.interface';
import { connect } from 'pwa-helpers';
import { css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { getUserDataForUserAnalytics } from '../user-profile/user-profile.selectors';
import { PROFESSIONAL } from '../../common/utils/util';

const LINK_CONFIG = {
  LINK_1: {
    label: 'here',
    url: 'https://www.adobe.com/solutions/ebook/digital-editions.html',
  },
  LINK_2: {
    label: 'account.Adobe.com',
    url: 'https://account.adobe.com',
  },
  LINK_3: {
    label: 'customer service',
    url: 'mailto:MHP-Ebooks@mheducation.com',
  },
};

@customElement('mhecomm-mhp-ebook-download')
export class MhpEbookDownload extends connect(store)(BaseComponent) {
  static styles = css`
    .adobe-digital-editions-instructions {
      line-height: 150%; /* 27px */
    }
  `;

  /**
   * An array of eBook URLs to download the content.
   */
  @property({ type: Array })
  eBookUrls?: EbookUrl[];

  /**
   * The ISBN of the eBook.
   */
  @property()
  isbn = '';

  /**
   * Additional classes on the CTA
   * added from the enclosing component
   */
  @property()
  ctaClass = '';

  /**
   * Additional classes on the container of the instructions
   * added from the enclosing component
   */
  @property()
  instructionsContainerClass = '';

  renderComp() {
    if (!this.eBookUrls || !this.eBookUrls.length) {
      return html``;
    }

    return html`
      <div class="mhp-ebooks-download mb-4 ${this.ctaClass}">
        ${this.eBookUrls.map(
          (eBookUrl) => html`
            <a
              part="download-mhp-ebooks-large"
              class="btn btn-primary"
              data-automation-id=${`mhp-ebook-download-${this.isbn}`}
              href=${eBookUrl.url}
              @click=${(e: Event) =>
                this.fireAnalytics(e, {
                  eventName: 'mhp-ebook-download',
                  label: 'Download eBook',
                  url: eBookUrl.url,
                  action: eBookUrl.label
                })}
            >
              ${eBookUrl.label}
            </a>
          `
        )}
      </div>
      <div class="mhp-ebooks-instructions ${this.instructionsContainerClass}">
        <div class="mb-2">
          <span class="fs-medium fw-bold"
            >You can read this eBook using Adobe Digital Editions on your Mac or PC desktop or laptop. Mobile devices
            are not recommended.</span
          >
        </div>
        <div class="adobe-digital-editions-instructions fs-small">
          <p class="mb-2">
            <strong>Step 1. Download Adobe Digital Editions</strong>
            Both PC and Mac users will need to download Adobe Digital Editions to access their eBook. You can download
            Adobe Digital Editions at Adobe's website
            <a
              href="${LINK_CONFIG.LINK_1.url}"
              aria-label="Adobe digital editions"
              @click=${(e: Event) =>
                this.fireAnalytics(e, {
                  eventName: 'click',
                  label: LINK_CONFIG.LINK_1.label,
                  url: LINK_CONFIG.LINK_1.url,
                })}
            >
              ${LINK_CONFIG.LINK_1.label}</a
            >.
          </p>
          <p class="mb-2">
            <strong>Step 2. Register an Adobe ID if you do not already have one.</strong>
            (Registration is optional, but allows you to open the file on multiple devices) Visit
            <a
              href="${LINK_CONFIG.LINK_2.url}"
              @click=${(e: Event) =>
                this.fireAnalytics(e, {
                  eventName: 'click',
                  label: LINK_CONFIG.LINK_2.label,
                  url: LINK_CONFIG.LINK_2.url,
                })}
            >
              ${LINK_CONFIG.LINK_2.label}</a
            >
            to register your Adobe account. Then, open Adobe Digital Editions, go to the Help menu, and select
            <strong>"Authorize Computer"</strong> to link your Adobe ID.
          </p>
          <p class="mb-2">
            <strong>Step 3. Open Your eBook.</strong>
            Use Adobe Digital Editions to open the file. If the eBook doesn’t open, contact
            <a
              class="blue-link"
              href="${LINK_CONFIG.LINK_3.url}"
              @click=${(e: Event) =>
                this.fireAnalytics(e, {
                  eventName: 'click',
                  label: LINK_CONFIG.LINK_3.label,
                  url: LINK_CONFIG.LINK_3.url,
                })}
              >${LINK_CONFIG.LINK_3.label}</a
            > for assistance.
          </p>
        </div>
      </div>
    `;
  }

  /**
   * Emits an event when the eBook download link is clicked.
   *
   * @private
   *  * @param {Event} event - The DOM event that triggered the analytics action. `preventDefault()` will be called on it.
   *  * @param {Object} options - Analytics options.
   *  * @param {string} options.eventName - The name of the analytics event to be emitted.
   *  * @param {string} [options.label] - Optional action label for the analytics event.
   *  * @param {string} [options.url] - Optional URL associated with the event.
   *  *
   *  * @returns {void}
   */
  private fireAnalytics(
    event: Event,
    {
      eventName,
      label,
      url,
      action
    }: {
      eventName: string;
      label?: string;
      url?: string;
      action?: string;
    }
  ): void {
    const state = store.getState() as StoreState;
    event.preventDefault();
    emit({
      element: this,
      name: eventName,
      detail: {
        comp: 'mhecomm-mhp-ebook-download',
        ...(action && { action }),
        ...(label && { label }),
        ...(url && { href: url }),
        url: window.location.href,
        user: getUserDataForUserAnalytics(state),
        businessUnit: PROFESSIONAL,
        orderType: PROFESSIONAL.toLowerCase(),
        ...(this.isbn && { isbn: this.isbn }),
      },
    });
    url && window.location.assign(url);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-mhp-ebook-download': MhpEbookDownload;
  }
}
